import React, { useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastProps {
  toast: ToastMessage;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // 진입 애니메이션
    const enterTimer = setTimeout(() => setIsVisible(true), 10);

    // 자동 닫기
    const duration = toast.duration || 5000;
    const closeTimer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(closeTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast.duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose(toast.id);
    }, 200);
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircleIcon className="toast-icon-size" />;
      case 'error':
        return <XCircleIcon className="toast-icon-size" />;
      case 'warning':
        return <ExclamationCircleIcon className="toast-icon-size" />;
      case 'info':
        return <InformationCircleIcon className="toast-icon-size" />;
    }
  };

  const getColors = () => {
    switch (toast.type) {
      case 'success':
        return {
          bg: 'var(--gcp-success-bg)',
          border: 'rgba(34, 197, 94, 0.3)',
          icon: 'var(--gcp-success)',
        };
      case 'error':
        return {
          bg: 'var(--gcp-error-bg)',
          border: 'rgba(239, 68, 68, 0.3)',
          icon: 'var(--gcp-error)',
        };
      case 'warning':
        return {
          bg: 'var(--gcp-warning-bg)',
          border: 'rgba(245, 158, 11, 0.3)',
          icon: 'var(--gcp-warning)',
        };
      case 'info':
        return {
          bg: 'var(--gcp-blue-light)',
          border: 'rgba(30, 142, 62, 0.3)',
          icon: 'var(--gcp-blue)',
        };
    }
  };

  const colors = getColors();

  return (
    <div
      role="alert"
      aria-live="polite"
      className="toast-container"
      style={{
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        transform: isLeaving
          ? 'translateX(100%)'
          : isVisible
          ? 'translateX(0)'
          : 'translateX(100%)',
        opacity: isLeaving ? 0 : isVisible ? 1 : 0,
      }}
    >
      {/* 아이콘 */}
      <div className="toast-icon" style={{ color: colors.icon }}>
        {getIcon()}
      </div>

      {/* 내용 */}
      <div className="toast-content">
        <p className="toast-title" style={{ marginBottom: toast.message ? '0.25rem' : 0 }}>
          {toast.title}
        </p>
        {toast.message && (
          <p className="toast-message">{toast.message}</p>
        )}
      </div>

      {/* 닫기 버튼 */}
      <button onClick={handleClose} aria-label="알림 닫기" className="toast-close-btn">
        <XMarkIcon className="toast-icon-size" />
      </button>
    </div>
  );
};

export default Toast;
